import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as SocketIO from 'socket.io-client'
import { useCookies } from 'react-cookie'
import { ApiAccess, UserSession } from '../types'
import { UserRole, UserRoleType } from 'interface/user'

export const sessionKey = `session_${process.env.NODE_ENV}`

const UserSessionContext = createContext<UserSession & {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasRole: (...roles: UserRoleType[]) => boolean
  isService: boolean
  isAdmin: boolean
  isGeneral: boolean
  isGuest: boolean
  isDemo: boolean
  isCareNet: boolean
  hasGenAI: boolean
}>(undefined as never)
export const useUserSession = () => useContext(UserSessionContext)

const ApiAccessContext = createContext<ApiAccess>(undefined as never)
export const useApiAccess = () => useContext(ApiAccessContext)

type UserSessionProviderProps = {
  children: ReactNode
  onLogout: () => void
}
export const UserSessionProvider = ({
  children,
  onLogout
}: UserSessionProviderProps) => {
  const [cookies] = useCookies([sessionKey])
  const [accessToken, setAccessToken] = useState<string | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loadUserSession = (session: any) => {
    if (!session) return null
    try {
      return JSON.parse(decodeURIComponent(session)) as UserSession
    } catch {
      return null
    }
  }
  const userSession = useMemo(
    () => loadUserSession(cookies[sessionKey]),
    [cookies]
  )
  const getApiAccess = useCallback(
    () => ({
      loginToken: userSession?.loginToken,
      accessToken,
      onGetAccessToken: setAccessToken,
      onExpiredAccessToken: () => setAccessToken(null),
      onUnAuthorize: (reason) => {
        console.log('onUnAuthorize', reason)
        onLogout()
      }
    }) as ApiAccess,
    [userSession, accessToken, setAccessToken, onLogout]
  )
  const hasRole = useCallback(
    (...roles: UserRoleType[]) => roles.some(role => userSession?.roles.includes(role)),
    [userSession]
  )
  useEffect(
    () => {
      if (!userSession) {
        onLogout()
      }
    },
    [userSession, onLogout]
  )
  if (!userSession) return <></>
  return (
    <UserSessionContext.Provider value={{
      ...userSession,
      hasRole,
      isService: hasRole(UserRole.Service),
      isAdmin: hasRole(UserRole.Admin, UserRole.Service),
      isGeneral: hasRole(UserRole.Admin, UserRole.Service, UserRole.General),
      isGuest: hasRole(UserRole.Guest),
      isDemo: hasRole(UserRole.Demo),
      isCareNet: hasRole(UserRole.CareNet) && userSession.inside
    }}>
      <ApiAccessContext.Provider value={getApiAccess()}>
        {children}
      </ApiAccessContext.Provider>
    </UserSessionContext.Provider>
  )
}

const WebSocketContext = createContext<SocketIO.Socket | null>(null)
export const useWebSockets = () => useContext(WebSocketContext)

type WebSocketProviderProps = {
  children: ReactNode
}
export const WebSocketProvider = ({
  children
}: WebSocketProviderProps) => {
  const socket = useMemo(
    () => SocketIO.io(
      process.env.REACT_APP_WEB_SERVER_URL,
      { withCredentials: true }
    ),
    []
  )
  return (
    <WebSocketContext.Provider value={socket}>
      {children}
    </WebSocketContext.Provider>
  )
}
